import React, { useState, useMemo, useContext, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  Progress,
  Spacer,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import {
  CrossIcon,
  RedCrossIcon,
  TickIcon,
  UploadIcon,
} from '../../../constants/IconData';
import {
  acceptStyle,
  baseStyle,
  focusedStyle,
  rejectStyle,
} from '../../chart_upload/styles';
import { UploadContext } from '../../../providers/ChartUploadProvider';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

const AddAdditionalDocsModal = ({ isOpen, onClose, auditUpload, onUploadComplete }) => {
  const chartUploadContext = useContext(UploadContext);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setFiles([])
    }
  }, [isOpen])

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: {
        'text/doc': ['.doc', '.docx', '.pdf', '.xls', '.xlsx', '.hl7'],
      },
      useFsAccessApi: false,
      onDrop: async acceptedFiles => {
        const newFiles = acceptedFiles.map(f => ({
          file: f,
          name: f.name,
          size: `${Math.floor(f.size / 1000)}kb`,
          value: 0,
          uploaded: false,
          cancelled: false,
        }));
        setFiles(prevFiles => [...prevFiles, ...newFiles]);
      },
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleSubmit = async () => {
    if (files.length > 0) {
      const chartId = auditUpload.id;
      const filesToUpload = files.map(f => f.file);
      await chartUploadContext.attachFiles(filesToUpload, chartId, (updatedFiles) => {
        let newFiles = [...files]
        newFiles.forEach((f, i) => {
          newFiles[i] = { ...newFiles[i], ...updatedFiles[i] }
        })
        setFiles(newFiles);
      });
      onUploadComplete();
    }
  };

  const handleRemoveFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Additional Documents</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Box
              {...getRootProps({ style })}
              p={10}
              borderWidth="2px"
              borderRadius="lg"
              borderStyle="dashed"
              width="100%"
              textAlign="center"
            >
              <input {...getInputProps()} />
              <UploadIcon style={{ width: 40, height: 40, margin: 'auto' }} />
              <Text mt={4}>
                Drag 'n' drop some files here, or click to select files
              </Text>
              <Text fontSize="sm" color="gray.500">
                Supported formats: XLS, XLSX, PDF, DOC, DOCX, HL7
              </Text>
            </Box>
            {files.length > 0 && (
              <Box width="100%">
                <Text mb={2} fontWeight="bold">
                  Files to upload
                </Text>
                <PerfectScrollbar style={{ maxHeight: '150px' }}>
                  {files.map((file, index) => (
                    <Box key={index} mt={2}>
                      <HStack>
                        <Text maxWidth="70%" isTruncated>
                          {file.name}
                        </Text>
                        <Spacer />
                        <Text>{file.size}</Text>
                        {file.uploaded && (
                          <TickIcon style={{ width: 22, height: 22 }} />
                        )}
                        {file.cancelled && (
                          <RedCrossIcon style={{ width: 22, height: 22 }} />
                        )}
                        {!file.uploaded && !file.cancelled && (
                          <CrossIcon
                            onClick={() => handleRemoveFile(index)}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </HStack>
                      <Progress
                        value={file.value}
                        size="xs"
                        colorScheme={file.cancelled ? 'red' : 'teal'}
                        borderRadius={10}
                      />
                    </Box>
                  ))}
                </PerfectScrollbar>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            ml={3}
            onClick={handleSubmit}
            isLoading={chartUploadContext.isSubmitting}
            disabled={
              files.length === 0 ||
              files.some(f => f.value > 0 && f.value < 100)
            }
          >
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddAdditionalDocsModal;
